/**
 * AI Expense Capture module dashboard (spec: expense-capture-module-dashboard).
 *
 * This module was the only one whose home page was never actually built — it fell through
 * to the generic ModuleDashboardPage fallback and rendered a heading plus two buttons,
 * while the other five modules all had real dashboards. This is that page.
 *
 * Built on the same DashboardWidgets primitives and the same layout grammar as
 * EsignDashboardPage, deliberately: the point is that this module stops looking like the
 * odd one out, which a hand-rolled layout of its own would not achieve.
 *
 * One currency throughout. Every figure is denominated in the tenant's primary currency,
 * resolved server-side over the six-month window; spend in other currencies is footnoted
 * rather than added in. See the C5-13 note in routers/expense_insights.py for why.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Camera, CreditCard, AlertTriangle, CheckCircle2, FileWarning, Wallet,
  ReceiptText, Building2, Sparkles, RefreshCw,
} from "lucide-react";

import { getExpenseDashboard, refreshInsightsNarrative, CurrencyTotal } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";
import { useToast } from "../context/ToastContext";

const NAVY = "#1e3a8a";
const TEAL = "#0f766e";
const AMBER = "#f59e0b";
const RED = "#dc2626";
const LIME = "#4d7c0f";
const PURPLE = "#7c3aed";

const card: React.CSSProperties = {
  background: "white", borderRadius: 10, padding: "22px 24px",
  border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const money = (n: number) => n.toLocaleString("en", { maximumFractionDigits: 0 });

/** "2d" / "3h" / "just now" — how long a capture has been sitting in the queue. */
function waitedFor(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d`;
  if (hours >= 1) return `${hours}h`;
  return "just now";
}

/** "Jul" from a "YYYY-MM" key, without constructing a Date in the local timezone. */
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthLabel(key: string): string {
  const month = Number(key.split("-")[1]);
  return MONTH_NAMES[month - 1] ?? key;
}

/** "+ ₹12,400 INR" — the money the primary-currency figures deliberately exclude. */
function excludedNote(others: CurrencyTotal[]): string | null {
  if (!others?.length) return null;
  return "+ " + others.map(o => `${o.currency_symbol}${money(o.total)} ${o.currency}`).join(", ") + " not shown";
}

function PctChange({ pct }: { pct: number | null }) {
  if (pct == null) return null;
  // Spend going UP is the bad direction here, hence red for positive — the inverse of the
  // recruitment/esign dashboards, where up is good. Same convention the Insights tab uses.
  return (
    <span style={{ color: pct >= 0 ? RED : "#16a34a", fontWeight: 600 }}>
      {pct >= 0 ? "▲" : "▼"} {Math.abs(pct)}%
    </span>
  );
}

export default function ExpenseCaptureDashboardPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["expense-dashboard"],
    queryFn: getExpenseDashboard,
    refetchInterval: 60_000,
  });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  const stats = data?.stats;
  const sym = data?.primary_currency_symbol ?? "฿";
  const queue = data?.queue ?? [];
  const trend = data?.trend ?? [];
  const hasTrend = trend.some(t => t.total > 0);
  const alerts = data?.budget_alerts ?? [];
  const branches = data?.top_branches ?? [];
  const narrative = data?.narrative;
  const canRefresh = user?.role === "admin" || user?.role === "admin_assistant";

  const spendNote = excludedNote(stats?.spend_other_currencies ?? []);
  const trendNote = excludedNote(data?.trend_other_currencies ?? []);
  const vat = stats?.vat_reclaimable_by_currency ?? [];
  // The ledger reports VAT per currency; the card shows the primary one and lets the
  // Ledger tab carry the rest, rather than cramming several currencies into one tile.
  const vatPrimary = vat.find(v => v.currency === data?.primary_currency) ?? vat[0];

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await refreshInsightsNarrative();
      await qc.invalidateQueries({ queryKey: ["expense-dashboard"] });
      qc.invalidateQueries({ queryKey: ["insights-narrative"] });
      toast("Insights refreshed", "success");
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not refresh insights", "error");
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
          {greeting}, {firstName} 👋
        </h1>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
          {dayStr} · Your capture queue at a glance
        </p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Needs Review" value={stats?.needs_review ?? 0} color={AMBER}
          icon={<FileWarning size={18} />} to="/ai-expense-capture?status=needs_review"
          subtitle="Low-confidence extraction" />
        <StatCard label="Ready to Confirm" value={stats?.ready_to_confirm ?? 0} color={TEAL}
          icon={<CheckCircle2 size={18} />} to="/ai-expense-capture?status=ready_to_confirm"
          subtitle="Awaiting your OK" />
        <StatCard label="Extraction Failed" value={stats?.extraction_failed ?? 0}
          color={(stats?.extraction_failed ?? 0) > 0 ? RED : TEAL}
          icon={<AlertTriangle size={18} />} to="/ai-expense-capture?status=extraction_failed"
          subtitle={(stats?.extraction_failed ?? 0) > 0 ? "Could not be read" : "None failed"} />
        <StatCard label="Spend This Month" value={`${sym}${money(stats?.spend_this_month ?? 0)}`} color={NAVY}
          icon={<Wallet size={18} />} to="/ai-expense-capture/insights"
          subtitle={
            stats?.spend_pct_change != null
              ? `${stats.spend_pct_change >= 0 ? "▲" : "▼"} ${Math.abs(stats.spend_pct_change)}% vs last month`
              : "No spend last month"
          } />
        <StatCard label="VAT Reclaimable" value={`${vatPrimary?.currency_symbol ?? sym}${money(vatPrimary?.total ?? 0)}`}
          color={PURPLE} icon={<ReceiptText size={18} />} to="/ai-expense-capture/ledger"
          subtitle={`${stats?.vat_reclaimable_count ?? 0} full tax invoice${(stats?.vat_reclaimable_count ?? 0) === 1 ? "" : "s"}`} />
      </div>

      {spendNote && (
        <div style={{ margin: "-14px 0 24px", fontSize: 12, color: "#64748b" }}>
          Spend and VAT are shown in {data?.primary_currency}. {spendNote}.{" "}
          <button onClick={() => navigate("/ai-expense-capture/ledger")} style={{
            background: "none", border: "none", padding: 0, color: NAVY,
            fontSize: 12, fontWeight: 600, cursor: "pointer", textDecoration: "underline",
          }}>See the ledger</button>
        </div>
      )}

      {alerts.length > 0 && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
          padding: "12px 16px", marginBottom: 24,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#854d0e", marginBottom: alerts.length ? 4 : 0 }}>
            ⚠ {alerts.length} categor{alerts.length === 1 ? "y" : "ies"} near or over budget
          </div>
          {alerts.map(a => (
            <div key={a.category} style={{ fontSize: 12, color: "#854d0e" }}>
              {a.category} at {a.budget_pct ?? 0}% of its {sym}{money(a.budget ?? 0)} monthly budget
            </div>
          ))}
        </div>
      )}

      <div style={{ ...card, padding: "20px 24px", marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Capture a Receipt" icon={<Camera size={14} />} to="/ai-expense-capture" color={LIME} />
          <QuickAction label="Ledger" icon={<ReceiptText size={14} />} to="/ai-expense-capture/ledger" color={NAVY} />
          <QuickAction label="Reimbursements" icon={<CreditCard size={14} />} to="/expenses" color={PURPLE} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }} className="grid-2col">
        <div style={card}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Review Queue</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Longest waiting first</div>
          </div>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>Loading…</div>
          ) : queue.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>
              Nothing waiting for review 🎉
            </div>
          ) : (
            queue.map((q, i) => (
              <div key={q.id} role="button" tabIndex={0}
                onClick={() => navigate(`/ai-expense-capture?capture=${q.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/ai-expense-capture?capture=${q.id}`);
                  }
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                  padding: "10px 0", borderBottom: i < queue.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: q.status === "needs_review" ? `${AMBER}18` : `${TEAL}18`,
                  color: q.status === "needs_review" ? AMBER : TEAL,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {q.status === "needs_review" ? <FileWarning size={14} /> : <CheckCircle2 size={14} />}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: "#334155",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    {q.vendor_name}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                    {q.status === "needs_review" ? "Needs review" : "Ready to confirm"}
                    {waitedFor(q.created_at) ? ` · waiting ${waitedFor(q.created_at)}` : ""}
                  </div>
                </div>
                {/* tabular-nums: these amounts stack into a right-aligned column, and
                    proportional digits make them visibly ragged row to row. */}
                <span style={{
                  fontSize: 13, fontWeight: 700, color: "#334155", flexShrink: 0,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {q.currency_symbol}{money(q.total)}
                </span>
              </div>
            ))
          )}
        </div>

        <div style={card}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Spend</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              Confirmed expenses · last 6 months · {data?.primary_currency ?? "THB"}
            </div>
          </div>
          {!hasTrend ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 13 }}>
              No confirmed spend yet
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={trend.map(t => ({ ...t, label: monthLabel(t.month) }))} barSize={26}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={40}
                    tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))} />
                  <Tooltip cursor={{ fill: "#f8fafc" }}
                    formatter={(v: number) => [`${sym}${money(v)}`, "Spend"]} />
                  <Bar dataKey="total" name="Spend" fill={NAVY} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {trendNote && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
                  Primary currency only. {trendNote}.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {narrative?.available !== false && (
        <div style={{ ...card, marginBottom: branches.length ? 24 : 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>What changed this month</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                AI summary of your confirmed spend
              </div>
            </div>
            {canRefresh && (
              <button onClick={handleRefresh} disabled={refreshing} style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "white", border: "1px solid #e2e8f0", borderRadius: 8,
                padding: "6px 12px", fontSize: 12, fontWeight: 600,
                color: refreshing ? "#94a3b8" : "#64748b",
                cursor: refreshing ? "default" : "pointer",
              }}>
                <RefreshCw size={13} /> {refreshing ? "Refreshing…" : "Refresh insights"}
              </button>
            )}
          </div>
          {(narrative?.insights ?? []).length === 0 ? (
            <div style={{ color: "#64748b", fontSize: 13 }}>Not enough activity yet for insights</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {narrative!.insights.map((text, i) => (
                <div key={i} style={{
                  background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 10,
                  padding: "12px 16px", fontSize: 13, color: "#6d28d9",
                  display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <Sparkles size={15} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {branches.length > 0 && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Top Branches</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>By spend this month</div>
            </div>
            <button onClick={() => navigate("/ai-expense-capture/branches")} style={{
              background: "none", border: "none", padding: 0, color: NAVY,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>
              Compare all →
            </button>
          </div>
          {branches.map((b, i) => {
            // Ranked server-side by largest single-currency amount; show that one and
            // signal the rest rather than adding currencies together.
            const top = b.amounts.reduce(
              (best, a) => (a.amount > best.amount ? a : best), b.amounts[0],
            );
            const more = b.amounts.length - 1;
            return (
              <div key={b.branch_id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 0", borderBottom: i < branches.length - 1 ? "1px solid #f1f5f9" : "none",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: `${NAVY}12`, color: NAVY,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Building2 size={14} />
                </div>
                <div style={{
                  minWidth: 0, flex: 1, fontSize: 13, fontWeight: 600, color: "#334155",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {b.branch_name}
                  {more > 0 && (
                    <span style={{ fontWeight: 500, color: "#64748b", marginLeft: 6, fontSize: 12 }}>
                      +{more} more currenc{more === 1 ? "y" : "ies"}
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 13, flexShrink: 0, display: "flex", gap: 8, alignItems: "baseline",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  <span style={{ fontWeight: 700, color: "#334155" }}>
                    {top?.currency_symbol}{money(top?.amount ?? 0)}
                  </span>
                  <PctChange pct={top?.pct_change_vs_last_month ?? null} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
